import { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import {
  pwd217Api,
  PendingItem,
  ApproveItem,
} from '../../../services/pwd217Api';

export interface UsePwd217ApprovalReturn {
  // Selection
  checkedKeys: string[];
  setCheckedKeys: (keys: string[]) => void;
  selectedDocNo: string | null;
  setSelectedDocNo: (key: string | null) => void;

  // Local editable state (mirrors items but with edits)
  editableItems: PendingItem[];
  setEditableItems: (items: PendingItem[]) => void;

  // Detail editing for selected item
  updateSupervisorDesc: (docNo: string, desc: string) => void;

  // Approve
  approving: boolean;
  handleApprove: (onDone: () => Promise<void>) => Promise<void>;

  // Computed
  selectedItem: PendingItem | undefined;
}

export function usePwd217Approval(items: PendingItem[]): UsePwd217ApprovalReturn {
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [selectedDocNo, setSelectedDocNo] = useState<string | null>(null);
  const [editableItems, setEditableItems] = useState<PendingItem[]>([]);
  const [approving, setApproving] = useState(false);

  // Sync editableItems when items change from query
  // The parent will call setEditableItems after fetch
  const selectedItem = useMemo(
    () => editableItems.find((it) => it.doc_no === selectedDocNo),
    [editableItems, selectedDocNo],
  );

  const updateSupervisorDesc = useCallback((docNo: string, desc: string) => {
    setEditableItems((prev) =>
      prev.map((it) => (it.doc_no === docNo ? { ...it, supervisor_desc: desc } : it)),
    );
  }, []);

  const handleApprove = useCallback(
    async (onDone: () => Promise<void>) => {
      if (checkedKeys.length === 0) {
        message.warning('請至少勾選一筆單據');
        return;
      }
      setApproving(true);
      try {
        const approveItems: ApproveItem[] = editableItems
          .filter((it) => checkedKeys.includes(it.doc_no))
          .map((it) => ({
            doc_no: it.doc_no,
            doc_type: it.doc_type,
            supervisor_desc: it.supervisor_desc,
            approved: true,
          }));

        const resp = await pwd217Api.approve(approveItems);

        if (resp.errors && resp.errors.length > 0) {
          message.warning(`核准 ${resp.approved_count} 筆，${resp.errors.length} 筆錯誤: ${resp.errors.join('; ')}`);
        } else {
          message.success(`已核准 ${resp.approved_count} 筆`);
        }

        setCheckedKeys([]);
        setSelectedDocNo(null);
        await onDone();
      } catch (err: unknown) {
        const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
        message.error(msg);
      } finally {
        setApproving(false);
      }
    },
    [checkedKeys, editableItems],
  );

  return {
    checkedKeys,
    setCheckedKeys,
    selectedDocNo,
    setSelectedDocNo,
    editableItems,
    setEditableItems,
    updateSupervisorDesc,
    approving,
    handleApprove,
    selectedItem,
  };
}
